/**
 * @file Shared mTLS configuration types.
 * @module @labcraft/core/shared/tls
 *
 * One `TlsProps` shape consumed on both sides of every intra-cluster
 * hop:
 *
 * - `core/backend`'s `HttpService` listens via `tls.createServer({
 *   cert, key, ca, requestCert: true, rejectUnauthorized: true })`
 *   when these PEMs are set in `HttpServiceProps.tls`.
 * - `core/frontend`'s `HttpClientTransport` presents the same PEMs
 *   via an `undici.Agent` dispatcher on Node-only consumers.
 * - Bao cert auth consumes the same bundle as the daemon's identity
 *   when calling the Bao API.
 *
 * Tiny shape (three PEM strings) so the dependency direction
 * `core/shared ← {core/backend, core/frontend}` stays clean — neither
 * side imports the other.
 *
 * Secret handling: `key` is the private half. Operators / `LoadCredential`
 * provide it as a plain PEM string, and `TlsPropsSchema.transform()`
 * wraps it in `CoreSecret` at parse time so any `JSON.stringify` /
 * `{...this}` spread path through `CoreClass`'s logging machinery
 * redacts it to `'[REDACTED]'`. Consumers that need the plaintext
 * (`tls.createServer`, `undici.Agent`, `node:https.request`) call
 * `.expose()` at the call site — visible at code review.
 */

import { z } from 'zod'
import { CoreSecret } from './core_secret.ts'

/**
 * mTLS configuration bundle in its **parsed** shape. `cert` and `ca`
 * are public and stay as plain strings; `key` is `CoreSecret`-wrapped
 * so it can't escape via accidental serialization. Use `key.expose()`
 * at the TLS handshake boundary.
 *
 * The **input** shape (what operators / env-var parsing produces) has
 * all three as plain strings — `z.input<typeof TlsPropsSchema>`. The
 * schema's `.transform()` lifts `key` into `CoreSecret` on parse.
 *
 * @property cert - PEM-encoded leaf certificate for this daemon.
 * @property key - PEM-encoded private key matching `cert`, wrapped.
 * @property ca - PEM-encoded CA bundle (one or more concatenated CAs)
 *   used to validate the *peer's* certificate.
 */
export type TlsProps = {
  cert: string
  key: CoreSecret
  ca: string
}

/**
 * Zod schema for `TlsProps`. Validates non-empty PEM strings at
 * config-parse time. `key` accepts EITHER a plain string (operator
 * env-var path) OR an existing `CoreSecret` (callers threading from
 * another `CoreSecret`-bearing source — e.g. a daemon bin passing
 * `config.tls.key` into a Client's props); plain strings are wrapped
 * on parse and `CoreSecret`s pass through, so the parsed output is
 * always `CoreSecret`. Cert / key / CA semantic validity is checked
 * by the platform TLS implementation (`tls.createServer` server-side,
 * `undici.Agent` client-side) at listener / first-request time.
 */
export const TlsPropsSchema = z.object({
  cert: z.string().min(1).describe('PEM-encoded leaf certificate'),
  key: z
    .union([
      z
        .string()
        .min(1)
        .transform(s => new CoreSecret(s)),
      z.instanceof(CoreSecret),
    ])
    .describe('PEM-encoded private key matching cert (wrapped in CoreSecret)'),
  ca: z
    .string()
    .min(1)
    .describe('PEM-encoded CA bundle (one or more concatenated CAs)'),
})
